import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@project/hooks/useAuth";
import { Check, HeartPulse, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@project/components/ui/button";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { openRazorpayCheckout } from "@project/donate/components/razorpay-checkout";
import {
  PaymentVerificationOverlay,
  type PaymentVerificationStatus,
} from "@project/donate/components/payment-verification-overlay";
import { getApiErrorMessage } from "@project/lib/api-error";
import { publicHealthcareDonationService } from "@project/services/donate/healthcare-donation.service";
import type { HealthcareCreateOrderPayload } from "@project/types/healthcare-donation";

export const HEALTHCARE_CATEGORIES = [
  {
    category: "Eye Care Clinic",
    sub_categories: [
      "Eye Care – Operation Support Donation",
      "Eye Care – Equipment & Machine Donation",
      "Eye Care – Netra Mani Donation",
      "Eye Care – Medical Absorbent Materials Donation",
    ],
  },
  {
    category: "Dental Clinic",
    sub_categories: [
      "Dental Care – Operation Support Donation",
      "Dental Care – Equipment & Machine Donation",
      "Dental Care – Medical Absorbent Materials Donation",
    ],
  },
  {
    category: "Medical Clinic",
    sub_categories: [
      "Medical Care – Operation Support Donation",
      "Medical Care – Equipment & Machine Donation",
      "Medical Care – Medical Absorbent Materials Donation",
    ],
  },
  {
    category: "Physiotherapy Centre",
    sub_categories: [
      "Physiotherapy – Equipment & Machine Donation",
      "Physiotherapy – Medical Absorbent Materials Donation",
    ],
  },
];

type FormState = {
  category: string;
  sub_category: string;
  amount: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  state: string;
  pincode: string;
  country: string;
  occasion: string;
  display_name: string;
  special_instructions: string;
  is_anonymous: boolean;
  info_confirmed: boolean;
  contact_consent: boolean;
};

const emptyForm = (initialCategory = "Medical Clinic"): FormState => ({
  category: initialCategory,
  sub_category: "",
  amount: "",
  donor_name: "",
  mobile_number: "",
  email: "",
  city: "",
  full_address: "",
  state: "Gujarat",
  pincode: "",
  country: "India",
  occasion: "",
  display_name: "",
  special_instructions: "",
  is_anonymous: false,
  info_confirmed: false,
  contact_consent: false,
});

export type HealthcareDonationModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hospitalName?: string;
  hospitalId?: string | number;
};

export function HealthcareDonationModal({
  open,
  onOpenChange,
  hospitalName = "",
  hospitalId,
}: HealthcareDonationModalProps) {
  // Determine initial category from hospitalName
  const initialCategory = useMemo(() => {
    const nameLower = hospitalName.toLowerCase();
    if (nameLower.includes("eye")) {
      return "Eye Care Clinic";
    }
    if (nameLower.includes("dental")) {
      return "Dental Clinic";
    }
    if (nameLower.includes("physiotherapy")) {
      return "Physiotherapy Centre";
    }
    return "Medical Clinic";
  }, [hospitalName]);

  const [form, setForm] = useState<FormState>(() => emptyForm(initialCategory));
  const [submitting, setSubmitting] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [verificationStatus, setVerificationStatus] =
    useState<PaymentVerificationStatus>("idle");

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setForm(emptyForm(initialCategory));
      setSubmitting(false);
      setVerificationOpen(false);
      setVerificationStatus("idle");
    }
  }, [open, initialCategory]);

  const patch = (updates: Partial<FormState>) =>
    setForm(prev => ({ ...prev, ...updates }));

  const subCategories = useMemo(() => {
    const selected = HEALTHCARE_CATEGORIES.find(
      item => item.category === form.category
    );
    return selected ? selected.sub_categories : [];
  }, [form.category]);

  // Pre-fill sub_category if categories list updates and current sub_category is not in list
  useEffect(() => {
    if (
      subCategories.length > 0 &&
      !subCategories.includes(form.sub_category)
    ) {
      setForm(prev => ({ ...prev, sub_category: subCategories[0] }));
    }
  }, [subCategories, form.sub_category]);

  const buildPayload = (): HealthcareCreateOrderPayload => {
    if (!form.info_confirmed) {
      throw new Error("Please confirm the information to proceed.");
    }
    if (!form.category) {
      throw new Error("Please select a donation category.");
    }
    if (!form.sub_category) {
      throw new Error("Please select a donation sub-category.");
    }
    const amtParsed = parseFloat(form.amount);
    if (Number.isNaN(amtParsed) || amtParsed <= 0) {
      throw new Error("Please enter a valid donation amount.");
    }
    if (!/^\d{10}$/.test(form.mobile_number.trim())) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    if (!/^\d{6}$/.test(form.pincode.trim())) {
      throw new Error("Enter a valid 6-digit pincode.");
    }

    return {
      category: form.category,
      sub_category: form.sub_category,
      amount: amtParsed.toFixed(2),
      donor_name: form.donor_name.trim(),
      mobile_number: form.mobile_number.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      full_address: form.full_address.trim(),
      state: form.state.trim(),
      pincode: form.pincode.trim(),
      country: form.country.trim() || "India",
      occasion: form.occasion.trim() || undefined,
      display_name: form.display_name.trim() || undefined,
      special_instructions: form.special_instructions.trim() || undefined,
      is_anonymous: form.is_anonymous,
      info_confirmed: form.info_confirmed,
      contact_consent: form.contact_consent,
      mode: "online",
    };
  };

  const handleProceed = async () => {
    if (!isAuthenticated) {
      toast.info("Please log in to continue with your donation");
      onOpenChange(false);
      navigate({
        to: "/login",
        search: {
          redirectTo: hospitalId ? `/healthcare/${hospitalId}` : "/healthcare",
        },
      });
      return;
    }

    try {
      const payload = buildPayload();
      setSubmitting(true);
      const order = await publicHealthcareDonationService.createOrder(payload);

      // Close the Radix Dialog BEFORE opening Razorpay so its iframe is not blocked
      onOpenChange(false);

      // Small tick so Radix finishes unmounting its overlay
      await new Promise(resolve => window.setTimeout(resolve, 80));

      await openRazorpayCheckout({
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency ?? "INR",
        key: order.key_id,
        name: "Jivandhara Healthcare",
        description: `${payload.category} - ${payload.sub_category}`,
        prefill: {
          name: payload.donor_name,
          email: payload.email,
          contact: payload.mobile_number,
        },
        onDismiss: () => {
          setSubmitting(false);
          // Reopen the modal so the user can try again
          onOpenChange(true);
          toast.message("Payment cancelled. You can try again anytime.");
        },
        onSuccess: async response => {
          setVerificationOpen(true);
          setVerificationStatus("verifying");
          try {
            const verify = await publicHealthcareDonationService.verifyPayment({
              razorpay_order_id: order.order_id,
              razorpay_payment_id: String(response.razorpay_payment_id),
              razorpay_signature: String(response.razorpay_signature),
            });
            setVerificationStatus("success");
            toast.success(
              verify.receipt_no
                ? `Payment successful. Receipt: ${verify.receipt_no}`
                : "Payment successful. Thank you for your support."
            );
            window.setTimeout(() => {
              setVerificationOpen(false);
              setVerificationStatus("idle");
            }, 1600);
          } catch (verifyError) {
            setVerificationOpen(false);
            setVerificationStatus("idle");
            toast.error(
              getApiErrorMessage(
                verifyError,
                "Payment received but verification failed. Please contact support."
              )
            );
          } finally {
            setSubmitting(false);
          }
        },
      });
    } catch (error) {
      setSubmitting(false);
      // Keep/reopen the modal so user can retry
      onOpenChange(true);
      toast.error(getApiErrorMessage(error, "Could not start payment"));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
          <DialogHeader className="shrink-0 border-b border-border px-5 py-4 pr-12 text-left sm:px-6">
            <div className="flex items-start gap-3">
              <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
                <HeartPulse className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-foreground sm:text-xl">
                  Healthcare Unit Donation Form
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-sm text-muted-foreground">
                  Support Jivandhara On-Campus Medical Services
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-7 overflow-y-auto px-5 py-5 sm:px-6">
            {/* Category selection */}
            <section className="space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Donation Details
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label data-required="true">Category</Label>
                  <Select
                    value={form.category}
                    onValueChange={category =>
                      patch({ category, sub_category: "" })
                    }
                  >
                    <SelectTrigger className="bg-muted/30 rounded-xl">
                      <SelectValue placeholder="Select Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {HEALTHCARE_CATEGORIES.map(item => (
                        <SelectItem key={item.category} value={item.category}>
                          {item.category}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label data-required="true">Sub-Category</Label>
                  <Select
                    value={form.sub_category}
                    onValueChange={sub_category => patch({ sub_category })}
                  >
                    <SelectTrigger className="bg-muted/30 rounded-xl">
                      <SelectValue placeholder="Select Sub-Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {subCategories.map(sub => (
                        <SelectItem key={sub} value={sub}>
                          {sub}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label data-required="true">Donation Amount (INR)</Label>
                  <Input
                    required
                    type="number"
                    min="1"
                    step="any"
                    value={form.amount}
                    onChange={e => patch({ amount: e.target.value })}
                    placeholder="Enter amount (e.g. 5000)"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
              </div>
            </section>

            {/* Your Details */}
            <section className="space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Your Details
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label data-required="true">Donor Name</Label>
                  <Input
                    required
                    value={form.donor_name}
                    onChange={e => patch({ donor_name: e.target.value })}
                    placeholder="Enter your full name"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Mobile Number</Label>
                  <MobileNumberField
                    required
                    value={form.mobile_number}
                    onChange={mobile_number => patch({ mobile_number })}
                    placeholder="10-digit phone number"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Email</Label>
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={e => patch({ email: e.target.value })}
                    placeholder="name@example.com"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">City</Label>
                  <Input
                    required
                    value={form.city}
                    onChange={e => patch({ city: e.target.value })}
                    placeholder="Ahmedabad"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label data-required="true">Full Address</Label>
                  <Textarea
                    required
                    rows={2}
                    value={form.full_address}
                    onChange={e => patch({ full_address: e.target.value })}
                    placeholder="Street name, society, flat number"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">State</Label>
                  <Input
                    required
                    value={form.state}
                    onChange={e => patch({ state: e.target.value })}
                    placeholder="Gujarat"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">Pincode</Label>
                  <Input
                    required
                    inputMode="numeric"
                    maxLength={6}
                    value={form.pincode}
                    onChange={e =>
                      patch({
                        pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      })
                    }
                    placeholder="380001"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Country</Label>
                  <Input
                    value={form.country}
                    onChange={e => patch({ country: e.target.value })}
                    placeholder="India"
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
              </div>
            </section>

            {/* Sponsorship Details */}
            <section className="space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Additional Details
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Occasion</Label>
                  <Input
                    value={form.occasion}
                    onChange={e => patch({ occasion: e.target.value })}
                    placeholder="e.g. Birthday, Anniversary, In memory of..."
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Name to Display on Board</Label>
                  <Input
                    value={form.display_name}
                    onChange={e => patch({ display_name: e.target.value })}
                    placeholder="In loving memory of... / On behalf of..."
                    className="bg-muted/30 rounded-xl"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Enter the donor, family, or commemorative name that may be
                    displayed.
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <div className="mb-1.5 flex items-center justify-between">
                    <Label>Special Instructions</Label>
                    <span className="text-[11px] text-muted-foreground">
                      {form.special_instructions.length}/500
                    </span>
                  </div>
                  <Textarea
                    rows={3}
                    maxLength={500}
                    value={form.special_instructions}
                    onChange={e =>
                      patch({ special_instructions: e.target.value })
                    }
                    placeholder="Any specific requests or instructions regarding your donation..."
                    className="bg-muted/30 rounded-xl"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="bg-muted/20 flex cursor-pointer items-start gap-3 rounded-xl border border-border px-4 py-3">
                    <Checkbox
                      checked={form.is_anonymous}
                      onCheckedChange={checked =>
                        patch({ is_anonymous: checked === true })
                      }
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-foreground">
                        Keep my donation anonymous
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Admin can still securely retain the donor&apos;s
                        details. Public recognition will display &apos;Anonymous
                        Donor&apos;.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </section>

            {/* Consent */}
            <section className="space-y-4">
              <h3 className="font-serif text-xl font-semibold text-foreground">
                Consent
              </h3>
              <div className="bg-muted/20 space-y-3 rounded-xl border border-border px-4 py-3">
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={form.info_confirmed}
                    onCheckedChange={checked =>
                      patch({ info_confirmed: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground">
                    I confirm that the information provided is correct and agree
                    to proceed with this donation.{" "}
                    <span className="text-destructive">*</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-3">
                  <Checkbox
                    checked={form.contact_consent}
                    onCheckedChange={checked =>
                      patch({ contact_consent: checked === true })
                    }
                    className="mt-0.5"
                  />
                  <span className="text-sm text-foreground">
                    I agree to be contacted regarding this Healthcare
                    sponsorship. (Optional)
                  </span>
                </label>
              </div>
            </section>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              <X className="mr-1 h-4 w-4" />
              Cancel
            </Button>
            <Button
              type="button"
              className="hover:bg-primary/90 rounded-xl bg-primary font-semibold text-primary-foreground"
              disabled={submitting}
              onClick={() => void handleProceed()}
            >
              <Check className="mr-1 h-4 w-4" />
              {submitting ? "Processing…" : "Proceed to Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <PaymentVerificationOverlay
        open={verificationOpen}
        status={verificationStatus}
      />
    </>
  );
}
